import type { GetCollectionPath } from '~~/types'
import type { ResourceStaticFiltersDefinitionObject } from '~~/types/filters'
import * as resourceFilterDefinitions from './resources'
export { type FilterKey, API_FILTERS } from './definitions'

export type SearchableGetCollectionPath = Extract<
  GetCollectionPath,
  | '/api/data/analyses'
  | '/api/data/analyses/absolute_dating'
  | '/api/data/analyses/{parentId}/absolute_dating'
  | '/api/data/analyses/botany/charcoals'
  | '/api/data/analyses/botany/seeds'
  | '/api/data/analyses/contexts/botany'
  | '/api/data/analyses/samples/botany'
  | '/api/data/analyses/sediment_core_depths/botany'
  | '/api/data/sediment_core_depths/{parentId}/analyses/botany'
  | '/api/data/analyses/individuals'
  | '/api/data/analyses/samples'
  | '/api/data/analyses/samples/microstratigraphy'
  | '/api/data/analyses/{parentId}/samples/microstratigraphy'
  | '/api/data/archaeological_sites'
  | '/api/data/archaeological_sites/{parentId}/stratigraphic_units'
  | '/api/data/archaeological_sites/{parentId}/contexts'
  | '/api/data/archaeological_sites/{parentId}/samples'
  | '/api/data/botany/charcoals'
  | '/api/data/botany/charcoals/{parentId}/analyses'
  | '/api/data/botany/seeds'
  | '/api/data/botany/seeds/{parentId}/analyses'
  | '/api/data/analyses/contexts/zoo'
  | '/api/data/analyses/potteries'
  | '/api/data/analyses/sediment_core_depths'
  | '/api/data/analyses/zoo/bones'
  | '/api/data/analyses/zoo/teeth'
  | '/api/data/context_stratigraphic_units'
  | '/api/data/contexts/{parentId}/stratigraphic_units'
  | '/api/data/history/animals'
  | '/api/data/history/plants'
  | '/api/data/history/written_sources'
  | '/api/data/history/written_sources/{parentId}/cited_works'
  | '/api/data/history/written_sources_cited_works'
  | '/api/data/individuals/{parentId}/analyses'
  | '/api/data/contexts'
  | '/api/data/contexts/{parentId}/analyses/botany'
  | '/api/data/samples/{parentId}/analyses/botany'
  | '/api/data/contexts/{parentId}/analyses/zoo'
  | '/api/data/individuals'
  | '/api/data/media_objects'
  | '/api/data/microstratigraphic_units'
  | '/api/data/paleoclimate_samples'
  | '/api/data/paleoclimate_sampling_sites'
  | '/api/data/paleoclimate_sampling_sites/{parentId}/samples'
  | '/api/data/potteries'
  | '/api/data/potteries/{parentId}/analyses'
  | '/api/data/samples'
  | '/api/data/sediment_core_depths/{parentId}/analyses'
  | '/api/data/samples/{parentId}/analyses'
  | '/api/data/samples/{parentId}/analyses/microstratigraphy'
  | '/api/data/samples/{parentId}/microstratigraphic_units'
  | '/api/data/samples/{parentId}/stratigraphic_units'
  | '/api/data/sample_stratigraphic_units'
  | '/api/data/sampling_sites'
  | '/api/data/sampling_sites/{parentId}/sediment_cores'
  | '/api/data/sampling_sites/{parentId}/stratigraphic_units'
  | '/api/data/sampling_sites/{parentId}/sediment_cores/depths'
  | '/api/data/sampling_stratigraphic_units'
  | '/api/data/stratigraphic_units'
  | '/api/data/sediment_cores'
  | '/api/data/sediment_core_depths'
  | '/api/data/sediment_cores/{parentId}/stratigraphic_units/depths'
  | '/api/data/stratigraphic_units/{parentId}/sediment_cores/depths'
  | '/api/sampling_sites/{parentId}/sediment_cores'
  | '/api/data/stratigraphic_units/{parentId}/analyses/samples/microstratigraphy'
  | '/api/data/stratigraphic_units/{parentId}/botany/charcoals'
  | '/api/data/stratigraphic_units/{parentId}/botany/seeds'
  | '/api/data/stratigraphic_units/{parentId}/contexts'
  | '/api/data/stratigraphic_units/{parentId}/individuals'
  | '/api/data/stratigraphic_units/{parentId}/microstratigraphic_units'
  | '/api/data/stratigraphic_units/{parentId}/potteries'
  | '/api/data/stratigraphic_units/{parentId}/samples'
  | '/api/data/stratigraphic_units/{parentId}/zoo/bones'
  | '/api/data/stratigraphic_units/{parentId}/zoo/teeth'
  | '/api/data/vocabulary/history/locations'
  | '/api/data/zoo/bones'
  | '/api/data/zoo/bones/{parentId}/analyses'
  | '/api/data/zoo/teeth'
  | '/api/data/zoo/teeth/{parentId}/analyses'
>

export const FILTERS_PATHS_MAP: Record<
  SearchableGetCollectionPath,
  ResourceStaticFiltersDefinitionObject
> = {
  '/api/data/analyses': resourceFilterDefinitions.analysis,
  '/api/data/analyses/absolute_dating':
    resourceFilterDefinitions.absDatingAnalysis,
  '/api/data/analyses/{parentId}/absolute_dating':
    resourceFilterDefinitions.absDatingAnalysis,
  '/api/data/analyses/botany/charcoals':
    resourceFilterDefinitions.analysisBotany,
  '/api/data/analyses/botany/seeds': resourceFilterDefinitions.analysisBotany,
  '/api/data/analyses/contexts/botany':
    resourceFilterDefinitions.analysisContextBotany,
  '/api/data/analyses/samples/botany':
    resourceFilterDefinitions.analysisSampleBotany,
  '/api/data/analyses/contexts/zoo':
    resourceFilterDefinitions.analysisContextZoo,
  '/api/data/analyses/individuals':
    resourceFilterDefinitions.analysisIndividual,
  '/api/data/analyses/potteries': resourceFilterDefinitions.analysisPottery,
  '/api/data/analyses/sediment_core_depths':
    resourceFilterDefinitions.analysisSedimentCoreDepth,
  '/api/data/analyses/sediment_core_depths/botany':
    resourceFilterDefinitions.analysisSedimentCoreDepthBotany,
  '/api/data/sediment_core_depths/{parentId}/analyses/botany':
    resourceFilterDefinitions.analysisSedimentCoreDepthBotanyParentSubject,
  '/api/data/analyses/samples': resourceFilterDefinitions.analysisSample,
  '/api/data/analyses/samples/microstratigraphy':
    resourceFilterDefinitions.analysisSampleMicrostratigraphicUnit,
  '/api/data/analyses/zoo/bones': resourceFilterDefinitions.analysisZooBone,
  '/api/data/analyses/zoo/teeth': resourceFilterDefinitions.analysisZooTooth,
  '/api/data/analyses/{parentId}/samples/microstratigraphy':
    resourceFilterDefinitions.analysisSampleMicrostratigraphicUnitParentAnalysis,
  '/api/data/archaeological_sites':
    resourceFilterDefinitions.archaeologicalSite,
  '/api/data/archaeological_sites/{parentId}/contexts':
    resourceFilterDefinitions.context,
  '/api/data/archaeological_sites/{parentId}/samples':
    resourceFilterDefinitions.sample,
  '/api/data/archaeological_sites/{parentId}/stratigraphic_units':
    resourceFilterDefinitions.stratigraphicUnit,
  '/api/data/botany/charcoals': resourceFilterDefinitions.botanyCharcoal,
  '/api/data/botany/charcoals/{parentId}/analyses':
    resourceFilterDefinitions.analysisBotanyParentSubject,
  '/api/data/botany/seeds': resourceFilterDefinitions.botanySeed,
  '/api/data/botany/seeds/{parentId}/analyses':
    resourceFilterDefinitions.analysisBotanyParentSubject,
  '/api/data/contexts': resourceFilterDefinitions.context,
  '/api/data/contexts/{parentId}/analyses/botany':
    resourceFilterDefinitions.analysisContextBotanyParentSubject,
  '/api/data/samples/{parentId}/analyses/botany':
    resourceFilterDefinitions.analysisSampleBotanyParentSubject,
  '/api/data/contexts/{parentId}/analyses/zoo':
    resourceFilterDefinitions.analysisContextZooParentSubject,
  '/api/data/contexts/{parentId}/stratigraphic_units':
    resourceFilterDefinitions.contextStratigraphicUnitParentContext,
  '/api/data/context_stratigraphic_units':
    resourceFilterDefinitions.contextStratigraphicUnit,
  '/api/data/history/animals': resourceFilterDefinitions.historyAnimal,
  '/api/data/vocabulary/history/locations':
    resourceFilterDefinitions.historyLocation,
  '/api/data/history/plants': resourceFilterDefinitions.historyPlant,
  '/api/data/history/written_sources':
    resourceFilterDefinitions.historyWrittenSource,
  '/api/data/history/written_sources/{parentId}/cited_works':
    resourceFilterDefinitions.historyWrittenSourceCitedWork,
  '/api/data/history/written_sources_cited_works':
    resourceFilterDefinitions.historyWrittenSourceCitedWork,
  '/api/data/individuals': resourceFilterDefinitions.individual,
  '/api/data/individuals/{parentId}/analyses':
    resourceFilterDefinitions.analysisIndividualParentSubject,
  '/api/data/media_objects': resourceFilterDefinitions.mediaObject,
  '/api/data/microstratigraphic_units':
    resourceFilterDefinitions.microstratigraphicUnit,
  '/api/data/paleoclimate_samples':
    resourceFilterDefinitions.paleoclimateSample,
  '/api/data/paleoclimate_sampling_sites':
    resourceFilterDefinitions.paleoclimateSamplingSite,
  '/api/data/paleoclimate_sampling_sites/{parentId}/samples':
    resourceFilterDefinitions.paleoclimateSample,
  '/api/data/potteries': resourceFilterDefinitions.pottery,
  '/api/data/potteries/{parentId}/analyses':
    resourceFilterDefinitions.analysisPotteryParentSubject,
  '/api/data/sediment_core_depths/{parentId}/analyses':
    resourceFilterDefinitions.analysisSedimentCoreDepthParentSubject,
  '/api/data/samples': resourceFilterDefinitions.sample,
  '/api/data/samples/{parentId}/analyses':
    resourceFilterDefinitions.analysisSampleParentSubject,
  '/api/data/samples/{parentId}/analyses/microstratigraphy':
    resourceFilterDefinitions.analysisSampleMicrostratigraphicUnitParentSubject,
  '/api/data/samples/{parentId}/microstratigraphic_units':
    resourceFilterDefinitions.microstratigraphicUnitParentStratigraphicUnit,
  '/api/data/samples/{parentId}/stratigraphic_units':
    resourceFilterDefinitions.sampleStratigraphicUnitParentSample,
  '/api/data/sampling_sites': resourceFilterDefinitions.samplingSite,
  '/api/data/sampling_sites/{parentId}/stratigraphic_units':
    resourceFilterDefinitions.samplingStratigraphicUnit,
  '/api/data/sediment_cores': resourceFilterDefinitions.sedimentCore,
  '/api/data/sampling_sites/{parentId}/sediment_cores':
    resourceFilterDefinitions.sedimentCore,
  '/api/data/sampling_sites/{parentId}/sediment_cores/depths':
    resourceFilterDefinitions.sedimentCoreDepth,
  '/api/data/sediment_core_depths': resourceFilterDefinitions.sedimentCoreDepth,
  '/api/data/sediment_cores/{parentId}/stratigraphic_units/depths':
    resourceFilterDefinitions.sedimentCoreDepth,
  '/api/data/stratigraphic_units/{parentId}/sediment_cores/depths':
    resourceFilterDefinitions.sedimentCoreDepth,
  '/api/data/sample_stratigraphic_units':
    resourceFilterDefinitions.sampleStratigraphicUnit,
  '/api/data/sampling_stratigraphic_units':
    resourceFilterDefinitions.samplingStratigraphicUnit,
  '/api/data/stratigraphic_units': resourceFilterDefinitions.stratigraphicUnit,
  '/api/data/stratigraphic_units/{parentId}/analyses/samples/microstratigraphy':
    resourceFilterDefinitions.analysisSampleMicrostratigraphicUnit,
  '/api/data/stratigraphic_units/{parentId}/botany/charcoals':
    resourceFilterDefinitions.botanyParentStratigraphicUnit,
  '/api/data/stratigraphic_units/{parentId}/botany/seeds':
    resourceFilterDefinitions.botanySeedParentStratigraphicUnit,
  '/api/data/stratigraphic_units/{parentId}/contexts':
    resourceFilterDefinitions.contextStratigraphicUnitParentStratigraphicUnit,
  '/api/data/stratigraphic_units/{parentId}/individuals':
    resourceFilterDefinitions.individualParentStratigraphicUnit,
  '/api/data/stratigraphic_units/{parentId}/microstratigraphic_units':
    resourceFilterDefinitions.microstratigraphicUnitParentStratigraphicUnit,
  '/api/data/stratigraphic_units/{parentId}/potteries':
    resourceFilterDefinitions.potteryParentStratigraphicUnit,
  '/api/data/stratigraphic_units/{parentId}/samples':
    resourceFilterDefinitions.sampleStratigraphicUnitParentStratigraphicUnit,
  '/api/data/stratigraphic_units/{parentId}/zoo/bones':
    resourceFilterDefinitions.zooBoneParentStratigraphicUnit,
  '/api/data/stratigraphic_units/{parentId}/zoo/teeth':
    resourceFilterDefinitions.zooToothParentStratigraphicUnit,
  '/api/data/zoo/bones': resourceFilterDefinitions.zooBone,
  '/api/data/zoo/bones/{parentId}/analyses':
    resourceFilterDefinitions.analysisZooBoneParentSubject,
  '/api/data/zoo/teeth': resourceFilterDefinitions.zooTooth,
  '/api/data/zoo/teeth/{parentId}/analyses':
    resourceFilterDefinitions.analysisZooToothParentSubject,
} as const

export const isSearchableGetCollectionPath = (
  value: unknown,
): value is SearchableGetCollectionPath =>
  typeof value === 'string' && value in FILTERS_PATHS_MAP
